import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building, GraduationCap, Users, Plus, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/format";

export default function Institutions() {
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    const { data } = await api.get("/institutions");
    setList(data);
  };
  useEffect(() => {
    load();
  }, []);

  const openInst = async (id) => {
    const { data } = await api.get(`/institutions/${id}`);
    setCurrent(data);
  };

  const addMember = async (form) => {
    try {
      await api.post(`/institutions/${current.id}/members`, form);
      toast.success("Member added");
      openInst(current.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeMember = async (mid) => {
    try {
      await api.delete(`/institutions/${current.id}/members/${mid}`);
      toast.success("Removed");
      openInst(current.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (current) {
    return (
      <InstitutionDetail
        inst={current}
        onBack={() => {
          setCurrent(null);
          load();
        }}
        onAdd={addMember}
        onRemove={removeMember}
      />
    );
  }

  return (
    <div className="space-y-8" data-testid="institutions-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            Partnerships
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            Institutions.
          </h1>
          <p className="text-zinc-400 mt-2">
            Schools, NGOs, colleges and corporates with bulk memberships.
          </p>
        </div>
        <NewInstitutionDialog open={openNew} setOpen={setOpenNew} onDone={load} />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.length === 0 && (
          <div className="col-span-full rounded-3xl border border-white/10 bg-white/[0.02] p-12 text-center text-zinc-500">
            No institutions yet. Create your first partnership.
          </div>
        )}
        {list.map((i) => (
          <button
            key={i.id}
            onClick={() => openInst(i.id)}
            data-testid={`inst-card-${i.id}`}
            className="text-left rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-[#D4B878]/40 transition"
          >
            <div className="flex items-center justify-between mb-3">
              <TypeBadge type={i.type} />
              <span className="text-xs text-zinc-500 capitalize">{i.status}</span>
            </div>
            <div className="text-xl font-bold">{i.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{i.city}</div>
            <div className="mt-5 pt-4 border-t border-white/5 flex justify-between items-end">
              <div>
                <div className="text-2xl font-black">
                  {i.member_count}
                  <span className="text-sm text-zinc-500 font-semibold">
                    /{i.member_cap}
                  </span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  Members
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-[#E8D6A3] font-bold">
                  {i.included_hours_per_member}h
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  / member
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TypeBadge({ type }) {
  const map = {
    school: { label: "School", icon: <GraduationCap size={12} /> },
    college: { label: "College", icon: <GraduationCap size={12} /> },
    ngo: { label: "NGO", icon: <Users size={12} /> },
    corporate: { label: "Corporate", icon: <Building size={12} /> },
  };
  const v = map[type] || { label: type, icon: null };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#D4B878]/30 bg-[#D4B878]/10 text-[#E8D6A3] text-[10px] uppercase tracking-[0.18em] font-bold">
      {v.icon} {v.label}
    </span>
  );
}

function NewInstitutionDialog({ open, setOpen, onDone }) {
  const [form, setForm] = useState({
    name: "",
    type: "school",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    city: "",
    member_cap: 50,
    included_hours_per_member: 20,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/institutions", {
        ...form,
        member_cap: +form.member_cap,
        included_hours_per_member: +form.included_hours_per_member,
      });
      toast.success("Institution created");
      setOpen(false);
      setForm({ ...form, name: "", contact_name: "", contact_email: "", contact_phone: "", city: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-institution-btn"
          className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
        >
          <Plus size={16} className="mr-2" /> New institution
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Create institution</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name">
              <Input data-testid="inst-name" value={form.name} onChange={set("name")} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={set("type")}>
                <SelectTrigger data-testid="inst-type" className="bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["school", "college", "ngo", "corporate"].map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact name">
              <Input data-testid="inst-contact-name" value={form.contact_name} onChange={set("contact_name")} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="City">
              <Input data-testid="inst-city" value={form.city} onChange={set("city")} className="bg-white/5 border-white/10" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <Input data-testid="inst-email" type="email" value={form.contact_email} onChange={set("contact_email")} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Phone">
              <Input data-testid="inst-phone" value={form.contact_phone} onChange={set("contact_phone")} className="bg-white/5 border-white/10" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Member cap">
              <Input data-testid="inst-cap" type="number" value={form.member_cap} onChange={set("member_cap")} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Hours / member">
              <Input data-testid="inst-hours" type="number" value={form.included_hours_per_member} onChange={set("included_hours_per_member")} className="bg-white/5 border-white/10" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || !form.name}
            data-testid="inst-create-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstitutionDetail({ inst, onBack, onAdd, onRemove }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role_tag: "student" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setBusy(true);
    await onAdd(form);
    setForm({ name: "", email: "", phone: "", role_tag: "student" });
    setBusy(false);
  };

  return (
    <div className="space-y-8" data-testid={`inst-detail-${inst.id}`}>
      <button
        onClick={onBack}
        className="text-zinc-400 hover:text-white inline-flex items-center gap-2 text-sm"
        data-testid="inst-back-btn"
      >
        <ArrowLeft size={16} /> Back to institutions
      </button>

      <div>
        <TypeBadge type={inst.type} />
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mt-3">
          {inst.name}
        </h1>
        <div className="text-zinc-500 mt-1">
          {inst.contact_name} · {inst.contact_email} · {inst.contact_phone} · {inst.city}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Stat label="Members" value={`${inst.members.length}/${inst.member_cap}`} />
        <Stat label="Hours/member" value={`${inst.included_hours_per_member}h`} />
        <Stat label="Hours used" value={`${inst.total_hours_used}h`} />
        <Stat
          label="Utilization"
          value={
            inst.members.length === 0
              ? "0%"
              : `${Math.round(
                  (inst.total_hours_used /
                    (inst.members.length * inst.included_hours_per_member)) *
                    100
                )}%`
          }
        />
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">
          Add member
        </div>
        <form onSubmit={submit} className="grid md:grid-cols-5 gap-3" data-testid="inst-add-member-form">
          <Input
            placeholder="Name"
            value={form.name}
            onChange={set("name")}
            data-testid="member-name"
            className="bg-white/5 border-white/10"
          />
          <Input
            placeholder="Email"
            type="email"
            value={form.email}
            onChange={set("email")}
            data-testid="member-email"
            className="bg-white/5 border-white/10"
          />
          <Input
            placeholder="Phone"
            value={form.phone}
            onChange={set("phone")}
            data-testid="member-phone"
            className="bg-white/5 border-white/10"
          />
          <Select value={form.role_tag} onValueChange={set("role_tag")}>
            <SelectTrigger data-testid="member-role" className="bg-white/5 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["student", "teacher", "volunteer", "employee"].map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="submit"
            disabled={busy}
            data-testid="member-add-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            Add
          </Button>
        </form>
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-zinc-500 uppercase text-[10px] tracking-[0.18em]">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Quota</th>
              <th className="text-left p-4">Used</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {inst.members.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-zinc-500">
                  No members yet.
                </td>
              </tr>
            )}
            {inst.members.map((m) => (
              <tr key={m.id} className="border-t border-white/5" data-testid={`inst-member-${m.id}`}>
                <td className="p-4 font-semibold">{m.name}</td>
                <td className="p-4 text-zinc-400">{m.email}</td>
                <td className="p-4 text-zinc-400 capitalize">{m.role_tag}</td>
                <td className="p-4 text-[#E8D6A3] font-bold">{m.hours_quota}h</td>
                <td className="p-4 text-zinc-400">{m.hours_used}h</td>
                <td className="p-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(m.id)}
                    data-testid={`member-remove-${m.id}`}
                    className="text-zinc-400 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="text-2xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
        {label}
      </div>
    </div>
  );
}
